import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload, FileText, X, Loader2, FileSpreadsheet, FileImage, File as FileIcon,
  Download, Lock, ShieldCheck,
} from "lucide-react";

const ICON_BY_MIME = (mime) => {
  if (!mime) return FileIcon;
  if (mime.startsWith("image/")) return FileImage;
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime.includes("csv")) return FileSpreadsheet;
  return FileText;
};

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.docx,.doc,.csv,.txt,application/pdf,image/*";

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * DocumentUpload — reusable secure document drop zone.
 *
 * Props:
 *  - missionId?: string         attach uploads to this mission (ACL enforced server-side)
 *  - onDocsChange?: (docs) => void  notified after every upload/remove
 *  - initialDocs?: array        initial list of documents (from API)
 *  - readOnly?: boolean         hide upload UI, show docs only
 *  - allowDownload?: boolean    show download buttons
 *  - title?: string             header title
 *  - description?: string       header sub-text
 */
export default function DocumentUpload({
  missionId,
  onDocsChange,
  initialDocs,
  readOnly = false,
  allowDownload = true,
  title = "Pièces du dossier",
  description = "Documents confidentiels — partagés uniquement avec le spécialiste assigné. Formats acceptés : PDF, JPG/PNG, XLSX, DOCX, CSV. Max 10 Mo par fichier.",
}) {
  const [docs, setDocs] = useState(initialDocs || []);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("autre");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    apiClient.get("/uploads/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (initialDocs) setDocs(initialDocs);
  }, [initialDocs]);

  const notify = (next) => {
    setDocs(next);
    onDocsChange?.(next);
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const next = [...docs];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} dépasse la limite de 10 Mo`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (missionId) fd.append("mission_id", missionId);
      try {
        const { data } = await apiClient.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.unshift(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Échec du transfert de ${file.name}`);
      }
    }
    notify(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeDoc = async (id) => {
    try {
      await apiClient.delete(`/uploads/${id}`);
      notify(docs.filter(d => d.id !== id));
      toast.success("Document retiré");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const { data } = await apiClient.get(`/uploads/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (readOnly) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div data-testid="document-upload">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-[#0B3A2A]">
            <ShieldCheck className="w-4 h-4 text-[#C9A961]" />
            <h3 className="font-medium">{title}</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">{description}</p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase tracking-wider">
          <Lock className="w-3 h-3" /> Chiffré en transit · Cloisonné
        </div>
      </div>

      {!readOnly && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-11 rounded-xl border-slate-200 bg-white sm:max-w-xs" data-testid="upload-category">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="upload-dropzone"
            className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-[#C9A961] rounded-2xl p-8 text-center transition-smooth bg-white hover:bg-[#FAFAF7]"
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              data-testid="upload-input"
              onChange={(e) => handleFiles(Array.from(e.target.files))}
            />
            {uploading ? (
              <>
                <Loader2 className="w-7 h-7 text-[#C9A961] mx-auto mb-3 animate-spin" />
                <div className="text-sm text-slate-600">Transfert sécurisé en cours…</div>
              </>
            ) : (
              <>
                <Upload className="w-7 h-7 text-[#C9A961] mx-auto mb-3" />
                <div className="text-sm text-[#0B3A2A] font-medium">Déposez vos pièces ou cliquez pour parcourir</div>
                <div className="text-xs text-slate-500 mt-1.5">PDF, JPG/PNG, XLSX, DOCX, CSV — 10 Mo max par fichier</div>
              </>
            )}
          </div>
        </>
      )}

      {docs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="upload-list">
          {docs.map(d => {
            const Icon = ICON_BY_MIME(d.mime);
            const cat = categories.find(c => c.id === d.category);
            return (
              <div key={d.id}
                className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3"
                data-testid={`upload-item-${d.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#C9A961]/10 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#C9A961]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0B3A2A] font-medium truncate">{d.filename}</div>
                  <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
                    <span>{formatBytes(d.size)}</span>
                    {cat && <span>· {cat.label}</span>}
                    <span>· {new Date(d.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {allowDownload && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => downloadDoc(d)}
                      data-testid={`upload-download-${d.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  {!readOnly && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-rose-600 hover:text-rose-700"
                      onClick={() => removeDoc(d.id)}
                      data-testid={`upload-remove-${d.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
